import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Radio } from "lucide-react";
import TrailerTeaserModal from "./TrailerTeaserModal";

export default function Hero({ data }) {
  const { hero } = data;
  const containerRef = useRef(null);
  const [teaserOpen, setTeaserOpen] = useState(false);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.6, 0.95]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-15%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  const lineVariants = {
    hidden: { y: "110%" },
    visible: (i) => ({
      y: "0%",
      transition: {
        delay: 0.9 + i * 0.12,
        duration: 1.1,
        ease: [0.22, 1, 0.36, 1],
      },
    }),
  };

  return (
    <section
      id="overview"
      ref={containerRef}
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden"
    >
      {/* Parallax background */}
      <motion.div
        style={{ y, scale }}
        className="absolute inset-0 w-full h-[120%]"
      >
        <img
          src={hero.backgroundImage}
          alt="The Protocol Lite key art"
          className="w-full h-full object-cover"
          loading="eager"
        />
      </motion.div>

      {/* Gradient overlays */}
      <motion.div
        style={{ opacity: overlayOpacity }}
        className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/30"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-transparent to-black/40" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_20%,black_85%)]" />

      {/* Content */}
      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 min-h-[100svh] flex flex-col justify-end px-6 md:px-12 lg:px-16 pb-16 md:pb-20 pt-32"
      >
        <div className="max-w-[1600px] mx-auto w-full">
          {/* Eyebrow + badges */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="flex flex-wrap items-center gap-3 mb-8 md:mb-12"
          >
            <span
              data-testid="hero-eyebrow"
              className="font-body text-[11px] md:text-xs uppercase tracking-[0.35em] text-accent"
            >
              {hero.eyebrow}
            </span>
            <span className="w-8 h-px bg-white/30" />
            {hero.badges.map((b, i) => (
              <span
                key={i}
                data-testid={`hero-badge-${i}`}
                className="font-body text-[10px] md:text-xs uppercase tracking-[0.25em] px-3 py-1.5 border border-white/20 text-white/80 backdrop-blur-sm bg-white/[0.03]"
              >
                {b}
              </span>
            ))}
          </motion.div>

          {/* Masked line-by-line reveal headline */}
          <h1
            data-testid="hero-title"
            className={`font-heading uppercase text-white ${
              hero.compact
                ? "text-5xl md:text-6xl lg:text-7xl"
                : "text-[15vw] md:text-[11vw] lg:text-[9.5vw]"
            } leading-[0.9] tracking-normal mb-6 md:mb-8 max-w-[16ch]`}
          >
            {hero.titleLines.map((line, i) => (
              <span key={i} className="mask-line">
                <motion.span
                  custom={i}
                  variants={lineVariants}
                  initial="hidden"
                  animate="visible"
                  className="block will-change-transform"
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          {/* Compact hero info stack */}
          {hero.compact && (hero.preorderInfo || hero.developmentInfo) && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.5, duration: 0.9 }}
              className="flex flex-col gap-3 mb-8 md:mb-10"
              data-testid="hero-info-stack"
            >
              {hero.preorderInfo && (
                <div className="inline-flex items-center gap-3 self-start">
                  <span className="w-2 h-2 rounded-full bg-accent animate-pulse" />
                  <span className="font-heading text-2xl md:text-3xl uppercase tracking-[0.1em] text-accent">
                    {hero.preorderInfo}
                  </span>
                </div>
              )}
              {hero.developmentInfo && (
                <div className="inline-flex items-center gap-3 self-start">
                  <span className="w-1.5 h-1.5 rounded-full bg-white/40" />
                  <span className="font-body text-sm md:text-base uppercase tracking-[0.25em] text-white/60">
                    {hero.developmentInfo}
                  </span>
                </div>
              )}
            </motion.div>
          )}

          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 lg:gap-16">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.7, duration: 0.9 }}
              className="font-body text-base md:text-lg text-white/70 max-w-xl leading-relaxed"
            >
              {hero.subtitle}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.9, duration: 0.9 }}
              className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3"
            >
              <button
                type="button"
                data-testid="trailer-status"
                onClick={() => setTeaserOpen(true)}
                className="group inline-flex items-center gap-4 border border-accent/40 bg-accent/[0.06] hover:bg-accent/[0.14] hover:border-accent backdrop-blur-sm px-6 py-4 transition-colors cursor-pointer"
              >
                <span className="relative flex items-center justify-center w-8 h-8 rounded-full border border-accent/40">
                  <Radio size={13} className="text-accent" />
                  <span className="absolute inset-0 rounded-full bg-accent/20 animate-ping" />
                </span>
                <span className="font-heading text-lg tracking-[0.15em] uppercase text-accent">
                  {hero.trailerStatus}
                </span>
              </button>
              {hero.preorderStatus && (
                <div
                  data-testid="preorder-status"
                  className="group inline-flex items-center gap-4 border border-white/25 bg-black/40 backdrop-blur-sm px-6 py-4"
                >
                  <span className="w-2 h-2 rounded-full bg-accent animate-pulse" />
                  <span className="font-heading text-lg tracking-[0.15em] uppercase text-white">
                    {hero.preorderStatus}
                  </span>
                </div>
              )}
            </motion.div>
          </div>

          {/* Bottom row: scroll indicator + meta */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2.4, duration: 1 }}
            className="mt-16 md:mt-24 flex items-end justify-between border-t border-white/10 pt-6"
          >
            <div className="flex items-center gap-3 text-white/50">
              <motion.span
                animate={{ y: [0, 6, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              >
                <ArrowDown size={14} />
              </motion.span>
              <span className="font-body text-[10px] uppercase tracking-[0.3em]">
                Scorri per esplorare
              </span>
            </div>
            <div className="hidden md:flex items-center gap-8 text-white/50 font-body text-[10px] uppercase tracking-[0.3em]">
              <span>Vol. 01</span>
              <span>Reveal — 12.2026</span>
              <span>A★001</span>
            </div>
          </motion.div>
        </div>
      </motion.div>

      <TrailerTeaserModal
        open={teaserOpen}
        onClose={() => setTeaserOpen(false)}
        data={data.trailerTeaser}
      />
    </section>
  );
}

